import { Suspense, lazy, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Button, Flex, Select } from 'antd';

import dayjs from 'dayjs';
import isEmpty from 'lodash.isempty';

import axios from '../../axios';

import { capitalize, downloadCSV } from '../../helpers/common';
import { sortDates, sortNumbers, sortStrings } from '../../helpers/filter';
import { hasPermission } from '../../helpers/user';

import { DATE_FORMAT, EDIT_COMPONENT_TYPE, SEARCH_THRESHOLD } from '../../constants/common';
import { BLANK_FILTER_TEXT } from '../../constants/livestock';
import messages from '../../constants/messages';
import { PERMISSIONS } from '../../constants/permissions';
import { TABLE_CONFIG_COLUMNS, TABLE_CONFIG_KEYS } from '../../constants/table-configurations';
import { MEASURES_CSV_TEMPLATE } from '../../constants/templates';
import { MEASURES_CSV_VALIDATION_SCHEMA } from '../../constants/validation-schemas';

import { useModal } from '../../hooks/useModal';
import { useUnsavedChangesPrompt } from '../../hooks/useUnsavedChangesPrompt';

import {
  createMeasurement,
  createMeasurements,
  deleteMeasurements,
  fetchAnimalMeasurements,
  fetchMeasurementTypes,
  fetchMeasurements,
  updateMeasurement
} from '../../redux/actions/measurement';
import { setSelectedFilters } from '../../redux/reducers/filters';
import { setSortedData } from '../../redux/reducers/measurement';

import AppTable from '../../components/AppTable';
import { COLUMN_SIZE } from '../../components/AppTable/constants';

import { errorToastHandler, infoToastHandler, successToastHandler, warnToastHandler } from '../action_notifier';
import CreateMeasurementsModal from './CreateMeasurementsModal';
import { useFilterOptions } from './useFilterOptions';

const AppModal = lazy(() => import('../../components/AppModal'));
const UploadModal = lazy(() => import('../../components/Modals/UploadModal'));
const UnsavedChangesModal = lazy(() => import('../../components/ui/UnsavedChangesModal'));

const commonColumnProperties = {
  ellipsis: true,
  sortDirections: ['ascend', 'descend']
};

function MeasurementsTable(props) {
  const dispatch = useDispatch();
  const history = useHistory();

  const formRef = useRef(null);
  const formCreateRef = useRef(null);

  const params = useParams();

  const {
    data: measurements,
    loading: measurementsLoading,
    types: measurementTypes
  } = useSelector(state => state.measurement);
  const { permissions } = useSelector(state => state.user);
  const { selectedFilters } = useSelector(state => state.filters);
  const { sortedData } = useSelector(state => state.measurement);

  const [createFormSubmittable, setCreateFormSubmittable] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    csv_modal: false,
    selectedIds: [],
    uploadSuccessMessage: ''
  });

  const [archiveModalVisible, openArchiveModal, closeArchiveModal] = useModal();
  const [createModalVisible, openCreateModal, closeCreateModal] = useModal();
  const [uploadSuccessModalVisible, openUploadSuccessModal, closeUploadSuccessModal] = useModal();
  const [updatedDataSource, setUpdatedDataSource] = useState(undefined);

  useEffect(() => {
    fetchData();

    return () => {
      dispatch(setSelectedFilters({}));
    };
  }, []);

  useEffect(() => {
    // on "Clear all filters" set to sortedData that used for calc filter options to original set of data
    if (isEmpty(filteredInfo) && measurements?.length) {
      dispatch(setSortedData(measurements));
    }
  }, [filteredInfo]);

  const toggleUploadModal = useCallback(() => {
    setState(prevState => ({ ...prevState, csv_modal: !prevState.csv_modal }));
    formRef.current?.resetFields();
  }, []);

  async function fetchData() {
    try {
      await Promise.all([dispatch(fetchMeasurementTypes()).unwrap(), getMeasurements()]);
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function getMeasurements(query) {
    params?.id ? dispatch(fetchAnimalMeasurements({ id: params?.id })) : dispatch(fetchMeasurements({ query }));
  }

  const clearFilters = useCallback(() => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  }, [setFilteredInfo]);

  const handleTableChange = useCallback(
    (pagination, filters, sorter, { currentDataSource }) => {
      setFilteredInfo(filters);
      dispatch(setSelectedFilters(filters));
      dispatch(setSortedData(currentDataSource));
    },
    [setFilteredInfo]
  );

  async function downloadCsv() {
    const url = state?.selectedIds?.length
      ? `/measures/export?measure_ids=${state?.selectedIds}`
      : '/measures/template_export';

    try {
      const response = await axios.get(url, { responseType: 'blob' });

      downloadCSV(response?.data, `Measures_${dayjs().format(DATE_FORMAT.DATE)}.xlsx`);
    } catch (error) {
      errorToastHandler(messages.FAILED_ON_DOWNLOAD_CSV);
    }
  }

  async function uploadCSV(csv) {
    try {
      const response = await axios.post('measures/uploadCsv', { csv });

      if (response?.status === 200) {
        setState(prevState => ({ ...prevState, uploadSuccessMessage: response.data.message }));
        await getMeasurements();
        toggleUploadModal();
        openUploadSuccessModal();
      }
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  async function handleChangeTableData([record]) {
    if (!record.measure_id) {
      infoToastHandler(messages.CREATE_EVENT('measurement'));
      setUpdatedDataSource([...measurements]);

      return;
    }

    const id = isNaN(record.measure_id) ? null : record.measure_id;
    const data = {
      ...record,
      id,
      comments: record.comments,
      livestock_id: parseInt(record.livestock_id),
      major_measure_id: parseInt(record.major_measure_id),
      measure_id: parseInt(record.measure_id),
      measurement: parseInt(record.measurement),
      measurement_date: dayjs(record.measurement_date).format(DATE_FORMAT.ISO_8601),
      minor_measure_id: parseInt(record.minor_measure_id)
    };

    try {
      await dispatch(updateMeasurement({ id, data })).unwrap();
      params.id && (await dispatch(fetchAnimalMeasurements(id)).unwrap());

      successToastHandler(messages.UPDATED);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  async function handleChangeMinorMeasureType(newValue, record) {
    const id = isNaN(record.measure_id) ? null : record.measure_id;
    const data = {
      id,
      livestock_id: parseInt(record.livestock_id),
      major_measure_id: parseInt(record.major_measure_id),
      measure_id: parseInt(record.measure_id),
      measurement_date: dayjs(record.measurement_date).format(DATE_FORMAT.ISO_8601),
      minor_measure_id: parseInt(newValue)
    };

    try {
      if (params.id) {
        await dispatch(updateMeasurement({ id, data })).unwrap();
        await dispatch(fetchAnimalMeasurements(id)).unwrap();
      } else {
        await dispatch(updateMeasurement({ id, data })).unwrap();
        await dispatch(fetchMeasurements()).unwrap();
      }

      successToastHandler(messages.UPDATED);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  const actions = useMemo(() => {
    const availableActions = [];

    if (hasPermission([PERMISSIONS.LIVESTOCK_MEASURES.CREATE], permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!params.id && !state.selectedIds?.length}
            onClick={openCreateModal}>
            Add measurement
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.LIVESTOCK_MEASURES.CREATE], permissions)) {
      availableActions.push({
        label: (
          <Button color="default" size="small" variant="link" onClick={toggleUploadModal}>
            Upload measurement
          </Button>
        )
      });
    }

    availableActions.push({
      label: (
        <Button color="default" size="small" variant="link" onClick={downloadCsv}>
          Download measurement
        </Button>
      )
    });

    if (hasPermission([PERMISSIONS.LIVESTOCK_MEASURES.DELETE_OWN], permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!state.selectedIds?.length}
            onClick={openArchiveModal}>
            Archive measurement
          </Button>
        )
      });
    }

    availableActions.push({
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!Object.values(selectedFilters)?.length}
          onClick={clearFilters}>
          Clear all filters
        </Button>
      )
    });

    return availableActions;
  }, [state.selectedIds, selectedFilters, permissions]);

  const columnFilterOptions = useFilterOptions(sortedData);

  const columns = [
    {
      ...commonColumnProperties,
      title: 'Measure ID',
      dataIndex: 'measure_identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.MEASURE_ID,
      filteredValue: filteredInfo.measure_identifier || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortStrings(a.measure_identifier, b.measure_identifier)
    },
    {
      ...commonColumnProperties,
      title: 'Major Measure Type',
      dataIndex: 'major_measurement_name',
      configColumnKey: TABLE_CONFIG_COLUMNS.MAJOR_MEASURE_TYPE,
      filteredValue: filteredInfo.major_measurement_name || null,
      filters: columnFilterOptions?.majorMeasurementNames,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) =>
        value === BLANK_FILTER_TEXT ? !record?.major_measurement_name : record?.major_measurement_name === value,
      sorter: (a, b) => sortStrings(a.major_measurement_name, b.major_measurement_name)
    },
    {
      ...commonColumnProperties,
      title: 'Minor Measure Type',
      dataIndex: 'minor_measurement_name',
      configColumnKey: TABLE_CONFIG_COLUMNS.MINOR_MEASURE_TYPE,
      filteredValue: filteredInfo.minor_measurement_name || null,
      filters: columnFilterOptions?.minorMeasurementNames,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) =>
        value === BLANK_FILTER_TEXT ? !record?.minor_measurement_name : record?.minor_measurement_name === value,
      sorter: (a, b) => sortStrings(a.minor_measurement_name, b.minor_measurement_name),
      render: (value, record) => {
        const majorMeasurementName = record.major_measurement_name || record.major_measure_name;
        const minorMeasurementName = record.minor_measurement_name || record.minor_measure_name;

        if (majorMeasurementName && minorMeasurementName && measurementTypes?.length) {
          const majors = measurementTypes.find(i => i.major_measurement_name === majorMeasurementName);

          const options = majors?.measure_minors?.map(i => ({
            label: i.minor_measurement_name,
            value: i.minor_measurement_id
          }));

          const current = majors?.measure_minors?.find(m => m.minor_measurement_name === minorMeasurementName);

          const valueObj = current
            ? { label: current.minor_measurement_name, value: current.minor_measurement_id }
            : null;

          return (
            <Select
              size="small"
              variant="borderless"
              style={{ height: '21px', width: '100%' }}
              options={options}
              showSearch={options?.length >= SEARCH_THRESHOLD}
              value={valueObj}
              onChange={newValue => handleChangeMinorMeasureType(newValue, record)}
            />
          );
        }
      }
    },
    {
      ...commonColumnProperties,
      title: 'UOM',
      dataIndex: 'uom',
      configColumnKey: TABLE_CONFIG_COLUMNS.UOM,
      filteredValue: filteredInfo.uom || null,
      searchable: true,
      width: COLUMN_SIZE.XS,
      sorter: (a, b) => sortStrings(a.uom, b.uom)
    },
    {
      ...commonColumnProperties,
      title: 'Measurement',
      dataIndex: 'measurement',
      configColumnKey: TABLE_CONFIG_COLUMNS.MEASUREMENT,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT_NUMBER },
      editable: true,
      filteredValue: filteredInfo.measurement || null,
      rules: [{ required: true, message: 'Required.' }, { type: 'number' }],
      searchable: true,
      width: COLUMN_SIZE.XS,
      onFilter: (value, record) => record.measurement === +value,
      sorter: (a, b) => sortNumbers(a.measurement, b.measurement)
    },
    {
      ...commonColumnProperties,
      title: 'Measurement Date',
      dataIndex: 'measurement_date',
      configColumnKey: TABLE_CONFIG_COLUMNS.MEASUREMENT_DATE,
      editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER },
      filteredValue: filteredInfo.measurement_date || null,
      rules: [{ required: true, message: 'Required.' }],
      searchable: true,
      width: COLUMN_SIZE.LG,
      render: (value, record) =>
        record.measurement_date ? dayjs(record.measurement_date).format(DATE_FORMAT.DATE) : null,
      sorter: (a, b) => sortDates(a.measurement_date, b.measurement_date)
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      configColumnKey: TABLE_CONFIG_COLUMNS.COMMENTS,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.comments || null,
      searchable: true,
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortStrings(a.comments, b.comments)
    }
  ];

  const livestockColumns = [
    {
      ...commonColumnProperties,
      title: 'Livestock ID',
      dataIndex: 'identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.LIVESTOCK_ID,
      filteredValue: filteredInfo.identifier || null,
      fixed: 'left',
      searchable: true,
      width: COLUMN_SIZE.MD,
      render: (value, record) => <Link to={`animal/${record.id}`}>{record.identifier}</Link>,
      sorter: (a, b) => sortStrings(a.identifier, b.identifier)
    },
    {
      ...commonColumnProperties,
      title: 'Mgmt Tag ID',
      dataIndex: 'eartag_management_id',
      configColumnKey: TABLE_CONFIG_COLUMNS.MGMT_TAG_ID,
      filteredValue: filteredInfo.eartag_management_id || null,
      searchable: true,
      width: COLUMN_SIZE.XL,
      sorter: (a, b) => sortStrings(a.eartag_management_id, b.eartag_management_id)
    },
    {
      ...commonColumnProperties,
      title: 'Sex',
      dataIndex: 'sex',
      configColumnKey: TABLE_CONFIG_COLUMNS.SEX,
      filteredValue: filteredInfo.sex || null,
      filters: columnFilterOptions?.sex,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.sex : record?.sex === value),
      render: value => capitalize(value),
      sorter: (a, b) => sortStrings(a.sex, b.sex)
    },
    {
      ...commonColumnProperties,
      title: 'Colour',
      dataIndex: 'colour',
      configColumnKey: TABLE_CONFIG_COLUMNS.COLOUR,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.INPUT
      },
      ellipsis: true,
      filteredValue: filteredInfo.colour || null,
      searchable: true,
      sortDirections: ['ascend', 'descend'],
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortStrings(a.colour, b.colour)
    },
    {
      ...commonColumnProperties,
      title: 'Breed',
      dataIndex: 'breed',
      configColumnKey: TABLE_CONFIG_COLUMNS.BREED,
      width: COLUMN_SIZE.XL,
      filters: columnFilterOptions?.breeds,
      filteredValue: filteredInfo.breed || null,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.breed : record?.breed === value),
      sorter: (a, b) => sortStrings(a.breed, b.breed)
    }
  ];

  const tableColumns = params?.id ? [...columns] : [...livestockColumns, ...columns];

  async function createMeasurementsForSelectedAnimals(data) {
    const measures = state.selectedIds?.map(livestock_id => ({
      ...data,
      livestock_id,
      measurement_date: dayjs(data.measurement_date).format(DATE_FORMAT.ISO_8601)
    }));

    await dispatch(createMeasurements({ measures })).unwrap();
    await dispatch(fetchMeasurements()).unwrap();
  }

  async function createMeasurementForCurrentAnimal(data) {
    const measures = {
      ...data,
      livestock_id: parseInt(params.id),
      measurement_date: dayjs(data.measurement_date).format(DATE_FORMAT.ISO_8601)
    };

    await dispatch(createMeasurement({ id: params.id, data: measures })).unwrap();
    await dispatch(fetchAnimalMeasurements({ id: params?.id })).unwrap();
  }

  async function handleCreateMeasurements() {
    if (!state.selectedIds?.length && !params.id) return;

    try {
      const values = await formCreateRef.current?.validateFields();
      state.selectedIds?.length && !params.id
        ? await createMeasurementsForSelectedAnimals(values)
        : await createMeasurementForCurrentAnimal(values);

      formCreateRef.current?.resetFields();
      setIsFormDirty(false);
      closeCreateModal();
      successToastHandler(messages.CREATED);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_CREATE_DATA);
    }
  }

  function selectRecords(selected) {
    setState(prevState => ({
      ...prevState,
      selectedIds: selected,
      selectAll: prevState.selectedIds?.length === selected?.length
    }));
  }

  async function deleteRecords() {
    try {
      await dispatch(deleteMeasurements(state.selectedIds)).unwrap();
      closeArchiveModal();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_DELETE_DATA);
    }
  }

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (createFormSubmittable) {
          await handleCreateMeasurements();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formCreateRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        formCreateRef.current?.resetFields();
        setIsFormDirty(false);
        closeCreateModal();
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : closeCreateModal();
  }, [isFormDirty]);

  return (
    <>
      {NavigationPrompt}
      <div className={props.className}>
        <AppTable
          headerClass="py-2"
          searchPlaceholder="Search measures"
          actions={actions}
          baseColumns={tableColumns}
          dataSource={measurements}
          description={props.breadcrumb}
          loading={measurementsLoading}
          searchable={props.searchable}
          tableConfigKey={TABLE_CONFIG_KEYS.LIVESTOCK_MEASURES}
          title={props.title}
          handleOnChange={handleTableChange}
          handleOnSelectRecords={selectRecords}
          handleSearchChange={getMeasurements}
          updateTableData={handleChangeTableData}
          updatedDataSource={updatedDataSource}
        />
      </div>

      <Suspense fallback={null}>
        <CreateMeasurementsModal
          formRef={formCreateRef}
          isOpen={createModalVisible}
          submittable={createFormSubmittable}
          handleCancel={handleCancel}
          handleConfirm={handleCreateMeasurements}
          onChange={setIsFormDirty}
          setCreateFormSubmittable={setCreateFormSubmittable}
        />

        <AppModal
          isOpen={archiveModalVisible}
          confirmButtonColor="danger"
          confirmButtonText="Archive"
          title="Archive measurement"
          handleCancel={closeArchiveModal}
          handleConfirm={deleteRecords}>
          <div className="py-4">{messages.ARCHIVE_CONFIRMATION('measures')}</div>
        </AppModal>

        <UploadModal
          fileName="measures-template.csv"
          formRef={formRef}
          isOpen={state.csv_modal}
          schema={MEASURES_CSV_VALIDATION_SCHEMA}
          submittable={submittable}
          template={MEASURES_CSV_TEMPLATE}
          onCancel={toggleUploadModal}
          onUpload={uploadCSV}
          setSubmittable={setSubmittable}
          description={
            <>
              <p className="mb-0">You can download the measures list to input new measures.</p>
              <p className="mb-0">Download and save the file, then choose this file to upload new measurements.</p>
            </>
          }
        />

        <AppModal
          confirmButtonText="Ok"
          cancelButtonHidden={true}
          isOpen={uploadSuccessModalVisible}
          handleConfirm={closeUploadSuccessModal}>
          <Flex align="center" justify="center" className="pb-2 pt-5">
            {state.uploadSuccessMessage}
          </Flex>
        </AppModal>

        <UnsavedChangesModal
          isOpen={modalProps.visible}
          handleCancel={modalProps.onCancel}
          handleConfirm={modalProps.onSave}
          handleDiscard={modalProps.onDiscard}
        />
      </Suspense>
    </>
  );
}

export default memo(MeasurementsTable);
